import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCallback } from 'react';

/**
 * Controls UX analytics collection via emp_config flag.
 * Default: DISABLED. No events are persisted unless explicitly activated.
 * 
 * Flag location: emp_config where configuracoes->>'ux_analytics_enabled' = 'true'
 */
export function useUxAnalyticsFlag(clusterId?: string) {
  const { user } = useAuth();

  const { data: isEnabled = false } = useQuery({
    queryKey: ['ux-analytics-flag', clusterId],
    queryFn: async () => {
      let query = supabase
        .from('emp_config')
        .select('id, cluster_id, configuracoes')
        .eq('modulo_ativo', true);

      if (clusterId) {
        query = query.eq('cluster_id', clusterId);
      }

      const { data, error } = await query;
      if (error || !data) return false;

      return data.some((config: any) => {
        const cfg = config.configuracoes || {};
        return cfg.ux_analytics_enabled === true;
      });
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  /**
   * Logs a UX event ONLY if analytics collection is enabled.
   * Fire-and-forget — never blocks the UI.
   * Uses correct emp_eventos_negocio schema: entidade, entidade_id, payload, user_id
   */
  const logUxEvent = useCallback(
    (params: {
      contrato_id: string;
      tipo_evento: string;
      entidade?: string;
      entidade_id?: string;
      payload?: Record<string, any>;
    }) => {
      if (!isEnabled || !user) return;

      supabase
        .from('emp_eventos_negocio')
        .insert({
          contrato_id: params.contrato_id,
          tipo_evento: params.tipo_evento,
          entidade: params.entidade || 'contrato',
          entidade_id: params.entidade_id || params.contrato_id,
          payload: params.payload || {},
          user_id: user.id,
        })
        .then(() => {});
    },
    [isEnabled, user]
  );

  return { isUxAnalyticsEnabled: isEnabled, logUxEvent };
}
